import throttle from 'lodash/throttle';

/* eslint-disable class-methods-use-this */
/**
 * @description C that make an element sticky it's manage by data attribute.
 *  "data-sticky" attribute allow to selecto the element.
 *  "data-sticky-position" allow you to choose the position of where the sticky show.
 *  "data-sticky-className" allow you to rewrite the stickyclassName if you want some custom class.
 *  "data-sticky-state" allow you to rewrite the sticky state class.
 *  "data-sticky-trigger" set the element that will make the sticky appear.
 *  "data-sticky-hide" set the element that will make the sticky disappear.
 * @param {HTMLElement} element - The target of that will be sticky.
 */
export default class Sticky {
    constructor(domElement) {
        if(!domElement) {
            throw new Error('element doesn\'t exist');
        }

        this.domElement = domElement;

        if(this.domElement.dataset.stickyInitialized === 'true') {
            throw new Error('Sticky is initialized');
        }

        this.domElement.dataset.stickyInitialized = 'true';

        this.configObject = this.classGenerator({
            className: this.domElement.dataset.stickyClassName,
            position: this.domElement.dataset.stickyPosition,
            state: this.domElement.dataset.stickyState
        });

        this.trigger = document.querySelector(this.domElement.dataset.stickyTrigger);
        this.hide = document.querySelector(this.domElement.dataset.stickyHide);

        this.init();
    }

    /**
     * @description This class generate the config class based on the css BEM syntax.
     * @param {object} configObject - Object that override the default config.
     * @returns {object} - Object with the default config.
     */
    classGenerator(configObject) {
        const self = {
            stickyClassName: configObject.className || 'sticky',
            stickyPosition: configObject.position || 'top',
            stickyState: configObject.state || 'show'
        };

        self.stickyPositionClass = `${self.stickyClassName}__${self.stickyPosition}`;
        self.stickyShowClassName = `${self.stickyClassName}__${self.stickyPosition}--${self.stickyState}`;

        return self;
    }

    /**
     * @description Add display logique css class to the sticky element based on the configObject.
     * @param {HTMLElement} domElement - Element that will have the basic class add.
     */
    classDispatcher(domElement) {
        const {stickyClassName, stickyPositionClass} = this.configObject;
        domElement.classList.add(stickyClassName);
        domElement.classList.add(stickyPositionClass);
    }

    /**
     * @description Check if the top of the window have meet the trigger point.
     * @param {HTMLElement} domTriggerPoint - Element that will trigger the sticky.
     * @returns {boolean} - If the triggerPoint has meet the top window then it returns true.
     */
    triggerOnTop(domTriggerPoint) {
        const trigger = domTriggerPoint || document.body;
        const elementPosition = trigger.offsetTop;

        if(window.pageYOffset > elementPosition) {
            return true;
        }

        return false;
    }

    /**
     * @description Hide trigger.
     * @param {HTMLElement} domHidePoint - The point where the sticky should disappear.
     * @returns {boolean} - Is trigger hide.
     */
    hideTrigger(domHidePoint) {
        return !((domHidePoint.offsetTop - window.innerHeight) < window.pageYOffset);
    }

    /**
     * @description Manage the display of the sticky.
     * @param {string} className - Class to add to the element that is sticky.
     * @param {boolean} isAddAction - True to add class, false to remove.
     */
    stickyClassDispatcher(className, isAddAction) {
        if(isAddAction) {
            this.domElement.classList.add(className);
        } else {
            this.domElement.classList.remove(className);
        }
    }

    /**
     * @description Add or remove given class.
     * @param {string} classToManage - The class to add or remove.
     */
    classManager(classToManage) {
        const isAddAction = this.hideTrigger(this.hide) ? this.triggerOnTop(this.trigger) : this.hideTrigger(this.hide);

        this.stickyClassDispatcher(classToManage, isAddAction);
    }

    /**
     * @description Init the logic of the sticky element.
     */
    handleScroll() {
        const {stickyShowClassName} = this.configObject;

        this.domElement.classList.remove('sticky--hidden');

        if(this.hide) {
            this.classManager(stickyShowClassName);
        } else {
            this.stickyClassDispatcher(stickyShowClassName, this.triggerOnTop(this.trigger));
        }
    }

    init() {
        this.classDispatcher(this.domElement);

        window.addEventListener('scroll', throttle(this.handleScroll.bind(this), 16));
    }
}
